import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useIntl, useSelector } from 'umi'
import { TableColumn } from 'tea-component'

import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble'
import { useLocal, useOption, useWeekDay, useWeekDayArr } from '@/pages/dashboard/utils/customHook'
import { commonDownloadValueFormatter, commonValueFormatter, formatExeclNum, formatFilterValues, formatNum, getWeekDayTitle, handleDownload } from '@/pages/dashboard/utils/utils'
import CardBox from '@/pages/dashboard/components/CardBox'
import MiniTable from '@/pages/dashboard/components/MiniTable'
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug'
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart'
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart'
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context'
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant'
import { getApiData } from '@/pages/dashboard/services/common'

const labelFormatX = (value) => (moment(value.split("(")[0]).format('MM-DD'))
// 格式化横坐标 label
const axisLabelFormat = (opt) => {
    return opt.xAxis?.map((item) => {
        return {
            ...item,
            axisLabel: {
                ...item.axisLabel,
                formatter: labelFormatX,
            },
        }
    })
}

function AveragePlaytimeDistributionCo(props: any) {
    const { config: propsConfig, className, style } = props;
    const { api, componentMetrics } = propsConfig || {}
    const { type = 'int', decimal = 0, enName, cnName } = componentMetrics || {}
    const contextObj = useContext(FilterContext)
    const { filterVal, filterLoading } = contextObj || {};
    const { gameCode } = useSelector((state: any) => {
        return {
            gameCode: state?.dashboard?.currentGame?.game_code,
        }
    });
    const { getWeekDay } = useWeekDay()
    const weekDayArr = useWeekDayArr();
    const { formatMessage } = useIntl() // 中英切换方法
    const { isEN, locale, getLocalText } = useLocal('engagement_active_online_distribution')
    const [onlineTimeDistributionData, setOnlineTimeDistributionData] = useState([])
    const [onlineTimeDistributionLoading, setOnlineTimeDistributionLoading] = useState(true)
    const [error, setError] = useState(false)
    const [ordersColumn, setOrdersColumn] = useState<string[]>([])

    const formatterFunc = (params) => {
        return getEchartTooltipText({
            title: `${params[0].axisValue}`,
            metricsName: '',
            data: params.map(({ seriesName, value, color }) => ({
                name: seriesName,
                metricsNum: commonValueFormatter(type, value, decimal),
                color,
            })),
        })
    }

    const config = {
        bar: {
            formatterFunc,
            setOtherConfigFunc: (opt) => {
                opt.legend.left = 'center'
                opt.legend.bottom = 0
                opt.xAxis = axisLabelFormat(opt)
                return opt
            },
        },
        line: {
            formatterFunc,
            setOtherConfigFunc: (opt) => {
                opt.legend.left = 'center'
                opt.legend.bottom = 0
                opt.xAxis = axisLabelFormat(opt)
                opt.series = opt.series?.map((item) => {
                    return {
                        ...item,
                        stack: '',
                        // showSymbol: false,
                    }
                })
                return opt
            },
        },
        table: {
            isSortable: true,
            setSortableConfig: (sorts, setSorts) => {
                return {
                    columns: [
                        {
                            key: 'date',
                            prefer: 'desc',
                            sorter: (first, second) => {
                                if (+moment(first.date) > +moment(second.date)) return 1
                                if (+moment(first.date) < +moment(second.date)) return -1
                                return 0
                            },
                        },
                        ...ordersColumn?.map((key) => ({
                            key,
                            prefer: 'desc',
                            sorter: (first, second) => first[key].replace(/[,-]/g, '') - second[key].replace(/[,-]/g, ''),
                        })),
                    ],
                    value: sorts,
                    onChange: (value, context) => setSorts(value.length ? [context.sort] : []),
                }
            },
        },
    }

    useEffect(() => {
        if (!filterVal) return;
        !filterLoading && getData();
    }, [filterVal, filterLoading]);

    const getData = () => {
        const { rangeTime, diff, timeType, ...filters } = filterVal || {};
        const params = {
            game_code: gameCode,
            start_time: rangeTime?.[0] || '',
            end_time: rangeTime?.[1] || '',
            by_type: 'accumulated',
            date_type: PERIODTIMEOBJ?.[timeType] || 'daily',
            filters: formatFilterValues(filters),
        }
        setOnlineTimeDistributionLoading(true)
        getApiData(api, params).then((res: any) => {
            setOnlineTimeDistributionLoading(false);
            setError(false);
            setOnlineTimeDistributionData(res?.otd_list ?? [])
        })
            .catch((err: any) => {
                setError(true);
                setOnlineTimeDistributionLoading(false);
                setOnlineTimeDistributionData([])
            });
    }

    const option = useMemo(() => {
        const otd_list = onlineTimeDistributionData
        // 图表数组
        let chart: any[] = []
        otd_list.forEach((cur, index) => {
            let { date, kv_list = [] } = cur
            kv_list?.forEach((item) => {
                const { key, value } = item
                const it = chart.find((el) => el.orignName === key)
                if (!it) {
                    chart.push({
                        orignName: key,
                        name: `[${key})`,
                        stack: 'bar',
                        data: [
                            {
                                x: getWeekDayTitle(date, weekDayArr),
                                y: value,
                            },
                        ],
                    })
                } else {
                    it?.data?.push({
                        x: getWeekDayTitle(date, weekDayArr),
                        y: value,
                    })
                }
            })
        })
        // 表格数据设置表头->取otd_list列表的第0项数据的otd_list
        const { kv_list = [] } = otd_list?.[0] || {}
        const setMap = new Set()
        kv_list?.forEach(({ key }) => {
            setMap.add(key)
        })
        // 表头名称
        const headerNames = [...setMap]
        setOrdersColumn(headerNames as string[])
        // 表头固定列
        const tabbleOption: TableColumn[] = [
            {
                key: 'date',
                align: 'left',
                fixed: 'left',
                width: 160,
                header: getLocalText('date'),
                render: (cvm) => {
                    const weekDay = getWeekDay(cvm?.date)
                    const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六'
                    return <>
                        <span className='table-text'>{cvm?.date}</span>
                        {filterVal?.timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
                    </>
                },
            },
        ]
        // 表头非固定列
        headerNames?.reduce((pre: any, key: any) => {
            pre.push({
                key,
                align: 'right',
                width: 150,
                header: key,
                render: (cvm) => <span>{cvm[key]}</span>,
            })
            return pre
        }, tabbleOption)
        // 表数据
        const tabbleData = otd_list.reduce((outPre, outCur) => {
            const { date, kv_list = [] } = outCur
            // row所对应的所有的column数据key->value
            const columnObj = kv_list?.reduce((pre, { key, value }) => {
                pre[key] = commonValueFormatter(type, value, decimal)
                return pre
            }, {} as any)
            outPre.push({
                date: date,
                ...columnObj,
            })
            return outPre
        }, [] as any[])
        const tabbleDownloadData = otd_list.reduce((outPre, outCur) => {
            const { date, kv_list = [] } = outCur
            // row所对应的所有的column数据key->value
            const columnObj = kv_list?.reduce((pre, { key, value }) => {
                pre[key] = commonDownloadValueFormatter(type, value, decimal)
                return pre
            }, {} as any)
            outPre.push({
                date: date,
                ...columnObj,
            })
            return outPre
        }, [] as any[])
        return {
            chart,
            table: {
                option: tabbleOption,
                data: tabbleData,
                downloadData: tabbleDownloadData,
            },
        }
    }, [locale, onlineTimeDistributionData])

    const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(option)
    const renderList = [
        {
            type: 'barChart',
            svgType: 'column-chart',
            content: formatMessage({ id: 'common_bar_chart' }),
            component: <BarChart
                height={340}
                legendData={xAxisData.length > 0 ? legendData : []}
                xAxisData={xAxisData || []}
                series={xAxisData.length > 0 ? barSeries : []}
                formatterFunc={config?.bar?.formatterFunc}
                setOtherConfigFunc={(opt: any) => {
                    if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt)
                    opt = xAxisHighLight(opt, {
                        formatter: (params: { value: string; }) => {
                            return labelFormatX(params?.value) ?? ''
                        }
                    })
                    return opt
                }}
            />
        },
        {
            type: 'lineChart',
            svgType: 'discount-chart',
            content: formatMessage({ id: 'common_line_chart' }),
            component: <LineChart
                height={340}
                legendData={xAxisData.length > 0 ? legendData : []}
                xAxisData={xAxisData || []}
                series={xAxisData.length > 0 ? lineSeries : []}
                formatterFunc={config?.line?.formatterFunc}
                setOtherConfigFunc={(opt: any) => {
                    if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt)
                    opt = xAxisHighLight(opt, {
                        formatter: (params: { value: string; }) => {
                            return labelFormatX(params?.value) ?? ''
                        }
                    })
                    return opt;
                }}
            />
        },
        {
            type: 'sheet',
            svgType: 'sheet',
            content: formatMessage({ id: 'common_table' }),
            component: <MiniTable
                height={340}
                loading={false}
                tableConfig={config?.table}
                tableOption={option?.table?.option}
                tableList={option?.table?.data}
            />
        },
    ]

    const title = isEN ? enName : cnName
    return (
        <div className={className} style={style}>
            <CardBox
                className='register-table'
                height={340}
                bordered={false}
                titleCom={title}
                renderList={renderList}
                defaultAvtive={0}
                downloadClick={async () => {
                    handleDownload(option?.table, title)
                }}
                isLoading={onlineTimeDistributionLoading}
                isError={error}
                isNodata={isNodata}
            />
        </div>
    )
}

export default AveragePlaytimeDistributionCo
